import React, { useState, useEffect } from 'react';
import { partnerService } from '../../services/partnerService';
import { imageService } from '../../services/imageService';
import { resolveAssetUrl } from '../../services/api';

const EMPTY_FORM = {
  name: '',
  website_url: '',
  sort_order: 0,
  is_active: true,
  logo_url: ''
};

export default function PartnersManager() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [logoUploading, setLogoUploading] = useState(false);

  const loadPartners = () => {
    setLoading(true);
    partnerService
      .getAllPartnersAdmin()
      .then((res) => {
        if (res.data) setPartners(res.data);
      })
      .catch((err) => alert(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadPartners();
  }, []);

  const handleOpenAdd = () => {
    setEditingItem(null);
    setFormData({
      ...EMPTY_FORM,
      sort_order: partners.length ? Math.max(...partners.map((p) => p.sort_order || 0)) + 1 : 1
    });
    setModalOpen(true);
  };

  const handleOpenEdit = (p) => {
    setEditingItem(p);
    setFormData({
      name: p.name || '',
      website_url: p.website_url || '',
      sort_order: p.sort_order ?? 0,
      is_active: p.is_active ?? true,
      logo_url: p.logo_url || ''
    });
    setModalOpen(true);
  };

  const handleDelete = async (id, name) => {
    if (window.confirm(`Xoá đối tác "${name}"?\n\nHành động không thể hoàn tác.`)) {
      try {
        await partnerService.deletePartner(id);
        loadPartners();
      } catch (err) {
        alert(err.message);
      }
    }
  };

  const handleMove = async (item, dir) => {
    const idx = partners.findIndex((p) => p.id === item.id);
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= partners.length) return;
    const other = partners[swapIdx];
    try {
      await Promise.all([
        partnerService.updatePartner(item.id, { sort_order: other.sort_order }),
        partnerService.updatePartner(other.id, { sort_order: item.sort_order })
      ]);
      loadPartners();
    } catch (err) {
      alert('Lỗi đổi thứ tự: ' + err.message);
    }
  };

  const handleLogoPick = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert('File quá lớn (>5MB)');
      return;
    }
    const fd = new FormData();
    fd.append('image', file);
    setLogoUploading(true);
    try {
      const res = await imageService.upload(fd);
      setFormData((prev) => ({ ...prev, logo_url: res.data.url }));
    } catch (err) {
      alert('Lỗi upload: ' + err.message);
    } finally {
      setLogoUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...formData, sort_order: Number(formData.sort_order) || 0 };
    try {
      if (editingItem) {
        await partnerService.updatePartner(editingItem.id, payload);
      } else {
        await partnerService.createPartner(payload);
      }
      setModalOpen(false);
      loadPartners();
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <section className="admin-panel">
      <div className="panel-head">
        <div>
          <h2>Đối tác đồng hành</h2>
          <p className="panel-head__sub">
            Quản lý mục <strong>Đối tác đồng hành</strong> ở trang chủ. Đổi thứ tự bằng nút ↑↓.
          </p>
        </div>
        <button className="btn btn--primary" onClick={handleOpenAdd} type="button">
          + Thêm đối tác
        </button>
      </div>

      {loading ? (
        <p className="muted">Đang tải…</p>
      ) : partners.length === 0 ? (
        <p className="muted">Chưa có đối tác nào. Bấm <strong>+ Thêm đối tác</strong> để tạo mới.</p>
      ) : (
        <div className="jobs-list">
          {partners.map((p, i) => (
            <div key={p.id} className={`job-row partner-row ${p.is_active ? '' : 'is-inactive'}`}>
              <div className="partner-row__logo">
                {p.logo_url ? (
                  <img src={resolveAssetUrl(p.logo_url)} alt={p.name} />
                ) : (
                  <span className="partner-row__logo-empty">—</span>
                )}
              </div>
              <div className="job-row__main">
                <span className="job-row__title">
                  {p.name}
                  <span className={`job-row__badge ${p.is_active ? '' : 'job-row__badge--off'}`}>
                    {p.is_active ? 'Đang hiện' : 'Đã ẩn'}
                  </span>
                </span>
                <span className="job-row__meta">
                  sort: {p.sort_order}
                  {p.website_url && (
                    <> · <a href={p.website_url} target="_blank" rel="noopener noreferrer">{p.website_url}</a></>
                  )}
                </span>
              </div>
              <div className="job-row__actions">
                <button className="icon-btn" type="button" disabled={i === 0} title="Lên" onClick={() => handleMove(p, -1)}>↑</button>
                <button className="icon-btn" type="button" disabled={i === partners.length - 1} title="Xuống" onClick={() => handleMove(p, 1)}>↓</button>
                <button className="icon-btn" type="button" title="Sửa" onClick={() => handleOpenEdit(p)}>✎</button>
                <button className="icon-btn icon-btn--danger" type="button" title="Xoá" onClick={() => handleDelete(p.id, p.name)}>🗑</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modalOpen && (
        <div className="admin-modal">
          <div className="admin-modal__backdrop" onClick={() => setModalOpen(false)} />
          <div className="admin-modal__panel admin-modal__panel--narrow" role="dialog" aria-modal="true">
            <button className="admin-modal__close" aria-label="Đóng" type="button" onClick={() => setModalOpen(false)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
            </button>
            <div className="admin-modal__body">
              <h2>{editingItem ? 'Sửa đối tác: ' + editingItem.name : 'Thêm đối tác'}</h2>
              <form onSubmit={handleSubmit} className="admin-form">
                <label className="field">
                  <span className="field__label">Tên thương hiệu <em>*</em></span>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="vd: Imexpharm"
                    required
                  />
                </label>

                <label className="field">
                  <span className="field__label">Website (optional)</span>
                  <input
                    type="url"
                    value={formData.website_url}
                    onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                    placeholder="https://imexpharm.com"
                  />
                </label>

                <div className="field-row">
                  <label className="field">
                    <span className="field__label">Sort order</span>
                    <input
                      type="number"
                      value={formData.sort_order}
                      onChange={(e) => setFormData({ ...formData, sort_order: e.target.value })}
                    />
                  </label>
                  <label className="field field--toggle">
                    <span className="field__label">Hiển thị public?</span>
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      />
                      <span></span>
                    </label>
                  </label>
                </div>

                <div className="field">
                  <span className="field__label">Logo <small>(3:2 · PNG/SVG nền trong suốt)</small></span>
                  <div className="cover-picker">
                    <div className="cover-preview cover-preview--logo">
                      {formData.logo_url ? (
                        <img src={resolveAssetUrl(formData.logo_url)} alt="logo preview" />
                      ) : (
                        <span className="cover-preview__hint">Chưa có logo — chỉ hiện tên</span>
                      )}
                    </div>
                    <div className="cover-picker__actions">
                      <label className="btn btn--ghost" style={{ cursor: logoUploading ? 'wait' : 'pointer' }}>
                        {logoUploading ? 'Đang upload…' : 'Chọn logo…'}
                        <input type="file" accept="image/jpeg,image/png,image/webp,image/svg+xml" hidden disabled={logoUploading} onChange={handleLogoPick} />
                      </label>
                      {formData.logo_url && (
                        <button type="button" className="btn btn--ghost" onClick={() => setFormData({ ...formData, logo_url: '' })}>
                          Bỏ logo
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="form-actions">
                  <button type="button" className="btn btn--ghost" onClick={() => setModalOpen(false)}>Huỷ</button>
                  <button type="submit" className="btn btn--primary">Lưu</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
